import React, { useState } from "react";
import axios from "axios";
import { authService } from "../services/auth";

const API_BASE = import.meta.env.VITE_API_BASE ?? "https://app.memesec-factory.com/api/v1";

interface GeneratedMeme {
  id: number;
  title: string;
  top_text: string;
  bottom_text: string;
  s3_url: string;
  web_url: string;
}

const MITRE_TACTICS = [
  "Reconnaissance", "Resource Development", "Initial Access", "Execution",
  "Persistence", "Privilege Escalation", "Defense Evasion", "Credential Access",
  "Discovery", "Lateral Movement", "Collection", "Command and Control",
  "Exfiltration", "Impact",
];

const Generate: React.FC = () => {
  const [topicHint, setTopicHint] = useState("");
  const [mitreId, setMitreId] = useState("");
  const [result, setResult] = useState<GeneratedMeme | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setResult(null);

    try {
      const { data } = await axios.post(
        `${API_BASE}/memes/generate`,
        { topic_hint: topicHint || undefined, mitre_technique_id: mitreId || undefined },
        { headers: { Authorization: `Bearer ${authService.getToken()}` } }
      );
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="generate-page">
      <h1>Generate a Meme</h1>
      <p className="subtitle">AI-powered InfoSec meme creation. Just add a topic hint and we do the rest.</p>

      <form onSubmit={handleGenerate} className="generate-form">
        <div className="form-group">
          <label>Topic Hint (optional)</label>
          <input
            type="text"
            value={topicHint}
            onChange={(e) => setTopicHint(e.target.value)}
            placeholder="e.g., lateral movement, phishing awareness, SOC burnout"
            maxLength={120}
          />
          <small>Hint the AI toward a specific InfoSec topic (max 120 chars)</small>
        </div>

        <div className="form-group">
          <label>MITRE ATT&CK Technique ID (optional)</label>
          <input
            type="text"
            value={mitreId}
            onChange={(e) => setMitreId(e.target.value)}
            placeholder="e.g., T1059.001"
          />
        </div>

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? "Generating..." : "Generate Meme"}
        </button>
      </form>

      {error && <div className="error-banner">{error}</div>}

      {result && (
        <div className="generate-result">
          <h2>Generated!</h2>
          <div className="result-meme">
            <img src={result.s3_url} alt={result.title} />
            <div className="result-meta">
              <p><strong>Title:</strong> {result.title}</p>
              <p><strong>Top:</strong> {result.top_text}</p>
              <p><strong>Bottom:</strong> {result.bottom_text}</p>
              <div className="result-actions">
                <a href={result.web_url} className="btn-primary">
                  View on Website
                </a>
                <a href={result.s3_url} target="_blank" rel="noreferrer" className="btn-secondary">
                  Open Image
                </a>
              </div>
              <div className="result-permalink">
                <label>Permalink</label>
                <input
                  type="text"
                  readOnly
                  value={result.web_url}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Generate;
